import BaseWidget from './baseWidget.js';
import { lines, niceCeil, euro, euroCompact, C_BLUE, C_RED } from './chartHelpers.js';
import stammdatenService from '../../services/stammdatenService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { getAktuellerZeitraum } from '../../util.js';

const VIEW_VERLAUF = 'verlauf';
const VIEW_TAGE = 'tage';

/**
 * Zeigt für jeden Tag des gewählten Monats Sollkosten (aus Dienstplan bzw. user-
 * gesetztem KostenSoll der Tagesübersicht) und Istkosten (aus tatsächlich
 * erfassten Schichten) – wahlweise als kumulierter Verlauf (zwei Linien) oder
 * als Tagesbalken (Ist je Tag, künftige Tage als Plan).
 *
 * Datenquelle: /api/dashboard/lohnkosten-trend, das in TagesuebersichtService
 * dieselbe Logik wie die einzelne Tagesübersicht über den ganzen Monat
 * aggregiert.
 */
export default class LohnkostenTrendWidget extends BaseWidget {
	getDefaultRowSpan() { return 1; }

	constructor(type, instanceId, state = {}) {
		super(type, instanceId, state);
		this.zeitraum = getAktuellerZeitraum();
		this.view = state.view === VIEW_TAGE ? VIEW_TAGE : VIEW_VERLAUF;
	}

	serializeState() {
		return { zeitraum: this.zeitraum, rowSpan: this.state?.rowSpan, view: this.view };
	}

	async onRender() {
		this.bodyEl.innerHTML = `
			<div class="widget-lohnkosten-wrap">
				<div class="widget-lohnkosten-toolbar">
					<div class="widget-lohnkosten-total">
						<span class="amount"></span>
						<span class="sub">Ist / Soll im Monat</span>
					</div>
					<label class="widget-lohnkosten-month" onmousedown='event.preventDefault(); this.querySelector("input").showPicker()'>
						<button class="widget-lohnkosten-nav-btn" type="button" aria-label="Vorheriger Monat" data-step="-1" onmousedown="event.stopPropagation()"><i class="bi bi-chevron-left"></i></button>
						<i class="bi bi-calendar3"></i>
						<input type="month" aria-label="Lohnkosten Trend Zeitraum" />
						<button class="widget-lohnkosten-nav-btn" type="button" aria-label="Nächster Monat" data-step="1" onmousedown="event.stopPropagation()"><i class="bi bi-chevron-right"></i></button>
					</label>
				</div>
				<div class="widget-lohnkosten-chart"></div>
				<span id="widget-lohnkosten-trend-legend" style="font-size: 10px; color: var(--muted);">* Geschätzte AG-Kosten aus Dienstplan und Zeiterfassung</span>
			</div>
		`;
		const input = this.bodyEl.querySelector('input[type="month"]');
		input.value = dayjs(this.zeitraum).format('YYYY-MM');
		const stepMonth = (delta) => {
			this.zeitraum = dayjs(this.zeitraum).add(delta, 'month').format('YYYY-MM-DD');
			input.value = dayjs(this.zeitraum).format('YYYY-MM');
			this.renderChart();
		};
		input.addEventListener('change', () => {
			if (!input.value) return;
			this.zeitraum = dayjs(input.value).format('YYYY-MM-DD');
			this.renderChart();
		});
		this.bodyEl.querySelectorAll('.widget-lohnkosten-nav-btn').forEach((btn) => {
			btn.addEventListener('click', () => stepMonth(Number(btn.dataset.step)));
		});
		this._installHeaderToggle();
		await this.renderChart();
	}

	/**
	 * Hängt die Verlauf/Tage-Umschaltung in die Tile-Kopfzeile (links neben
	 * Refresh/Close) statt in den Body. Damit bleibt im Body mehr Platz für das
	 * Diagramm und die Steuerung sitzt da, wo Nutzer Widget-weite Aktionen
	 * erwarten. Wir greifen über `bodyEl.closest('.dashboard-widget')` hoch ins
	 * Tile – BaseWidget bietet bewusst keinen Header-Hook, deshalb DOM-Injection.
	 */
	_installHeaderToggle() {
		const header = this.bodyEl.closest('.dashboard-widget')?.querySelector('.dashboard-widget-header');
		if (!header) return;
		let toggle = header.querySelector('.widget-lohnkosten-toggle');
		if (!toggle) {
			toggle = document.createElement('div');
			toggle.className = 'widget-lohnkosten-toggle';
			toggle.setAttribute('role', 'tablist');
			toggle.innerHTML = `
				<button type="button" data-view="${VIEW_VERLAUF}" role="tab">Verlauf</button>
				<button type="button" data-view="${VIEW_TAGE}" role="tab">Tage</button>
			`;
			const actions = header.querySelector('.dashboard-widget-actions');
			if (actions) actions.insertAdjacentElement('beforebegin', toggle);
			else header.appendChild(toggle);
		}
		// Bei jedem onRender() (z.B. nach refresh) alte Listener verwerfen, indem
		// wir die Buttons klonen-und-ersetzen. Verhindert Mehrfach-Bindung.
		toggle.querySelectorAll('button').forEach((btn) => btn.replaceWith(btn.cloneNode(true)));
		toggle.querySelectorAll('button').forEach((btn) => {
			btn.classList.toggle('active', btn.dataset.view === this.view);
			btn.addEventListener('click', () => {
				if (this.view === btn.dataset.view) return;
				this.view = btn.dataset.view;
				toggle.querySelectorAll('button').forEach((b) => b.classList.toggle('active', b.dataset.view === this.view));
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
				// Nur das Diagramm neu zeichnen, Datenholen ist nicht nötig –
				// rows + Summen stehen unverändert für beide Ansichten.
				this._renderViewFromCache();
			});
		});
	}

	async renderChart() {
		const chartEl = this.bodyEl.querySelector('.widget-lohnkosten-chart');
		const totalEl = this.bodyEl.querySelector('.widget-lohnkosten-total .amount');
		if (!chartEl) return;
		chartEl.innerHTML = '<div class="widget-empty">Lade...</div>';
		if (totalEl) totalEl.textContent = '';
		this._cache = null;
		const betriebID = stammdatenService.aktuelleBetriebsstaette;
		if (!betriebID) {
			chartEl.innerHTML = '<div class="widget-empty">Keine Betriebsstätte ausgewählt.</div>';
			return;
		}
		try {
			const params = new URLSearchParams({ zeitraum: this.zeitraum });
			const url = `/neolohn/api/dashboard/lohnkosten-trend/${betriebID}?${params.toString()}`;
			const response = await fetch(url);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const rows = await response.json();
			if (!Array.isArray(rows) || rows.length === 0) {
				chartEl.innerHTML = '<div class="widget-empty">Keine Lohnkostendaten für diesen Zeitraum.</div>';
				return;
			}
			this._cache = this._buildCache(rows);
			if (totalEl) {
				const fmt = (v) => euro(v, { maximumFractionDigits: 0 });
				totalEl.textContent = `${fmt(this._cache.istGesamt)} / ${fmt(this._cache.sollGesamt)}`;
			}
			this._renderViewFromCache();
		} catch (err) {
			console.error('LohnkostenTrendWidget render failed:', err);
			chartEl.innerHTML = '<div class="widget-empty">Fehler beim Laden der Lohnkosten.</div>';
		}
	}

	/**
	 * Berechnet alle Werte, die beide Ansichten brauchen, einmal aus rows:
	 * kumulierte Soll-/Ist-Linien (mit NaN ab morgen) für Verlauf,
	 * Spitzentag + Today-Index + Wochentag-Flags für die Tagesbalken.
	 */
	_buildCache(rows) {
		const today = dayjs().startOf('day');
		let sollCum = 0;
		let istCum = 0;
		let egfzCum = 0;
		let sollUntilToday = 0;
		const sollValues = [];
		const egfzValues = [];
		const istValues = [];
		let todayIdx = -1;
		let peakIdx = -1;
		let peakIst = -Infinity;
		const days = rows.map((r, i) => {
			const datum = dayjs(r.Datum);
			const isFuture = datum.isAfter(today);
			sollCum += r.KostenSoll || 0;
			sollValues.push(Math.round(sollCum));
			egfzCum += r.KostenEGFZ || 0;
			egfzValues.push(Math.round(egfzCum));
			if (isFuture) {
				istValues.push(NaN);
			} else {
				const ist = r.KostenIst || 0;
				istCum += ist;
				istValues.push(Math.round(istCum));
				sollUntilToday = sollCum;
				if (ist > peakIst) { peakIst = ist; peakIdx = i; }
			}
			if (datum.isSame(today, 'day')) todayIdx = i;
			return {
				date: r.Datum,
				day: datum.date(),
				weekend: datum.day() === 0 || datum.day() === 6,
				future: isFuture,
				soll: r.KostenSoll || 0,
				ist: r.KostenIst || 0,
				egfz: r.KostenEGFZ || 0,
			};
		});
		return {
			rows,
			days,
			labels: rows.map((r) => dayjs(r.Datum).format('D')),
			sollValues,
			istValues,
			egfzValues,
			sollGesamt: sollCum,
			istGesamt: istCum,
			sollUntilToday,
			todayIdx,
			peakIdx,
			isCurrentMonth: today.isSame(dayjs(this.zeitraum), 'month'),
		};
	}

	_renderViewFromCache() {
		const chartEl = this.bodyEl.querySelector('.widget-lohnkosten-chart');
		if (!chartEl || !this._cache) return;
		if (this.view === VIEW_TAGE) {
			chartEl.innerHTML = this._renderBarsHtml(this._cache);
		} else {
			chartEl.innerHTML = this._renderLinesHtml(this._cache);
		}
	}

	_renderLinesHtml(c) {
		// Wochenraster + heutiger Tag, sofern er nicht zu dicht an einem Wochen-Label
		// liegt (Mindestabstand 3 Tage, sonst überlappen die Texte bei ~9 px Spaltenbreite).
		const fixedDays = [1, 7, 14, 21, 28];
		const shownDays = new Set(fixedDays.map(String));
		if (c.todayIdx >= 0) {
			const todayDay = dayjs(c.rows[c.todayIdx].Datum).date();
			const collides = fixedDays.some((d) => Math.abs(d - todayDay) < 3);
			if (!collides) shownDays.add(String(todayDay));
		}
		// Im laufenden Monat zeigen wir das Soll als zwei Legenden-Einträge:
		// grauer Punkt = "Soll heute" (greift visuell die graue heute-Markierungslinie auf),
		// blauer Punkt = "Soll Monat" (die tatsächlich gezeichnete blaue Soll-Linie).
		// Die "Soll heute"-Serie hat keine Werte (NaN), erscheint also nur in der Legende
		// und zeichnet keine zusätzliche Linie.
		const TODAY_MARKER_COLOR = '#5b6b7e';
		const series = [
			{ name: 'Ist', color: C_RED, values: c.istValues, legendValue: c.istGesamt },
		];
		if (c.isCurrentMonth) {
			series.push({
				name: 'Soll heute',
				color: TODAY_MARKER_COLOR,
				values: c.sollValues.map(() => NaN),
				legendValue: c.sollUntilToday,
			});
			series.push({
				name: 'Soll Monat',
				color: C_BLUE,
				values: c.sollValues,
				legendValue: c.sollGesamt,
				dash: true,
			});
		} else {
			series.push({
				name: 'Soll',
				color: C_BLUE,
				values: c.sollValues,
				legendValue: c.sollGesamt,
				dash: true,
			});
		}
		const fmt = (v) => euro(v, { maximumFractionDigits: 0 });
		return lines(
			series,
			c.labels,
			{
				format: euroCompact,
				legendFormat: fmt,
				xlabelFilter: (label) => shownDays.has(label),
				markerIndex: c.todayIdx >= 0 ? c.todayIdx : undefined,
				// Pro Spalte: Datum + kumulierte Werte. Ist entfällt für zukünftige Tage
				// (dort ist der Wert NaN, weil die Linie ab heute abbricht).
				tooltip: (i) => {
					const datum = dayjs(c.rows[i].Datum).format('dd, D. MMMM');
					const istPart = Number.isFinite(c.istValues[i]) ? ` · Ist ${fmt(c.istValues[i])}` : '';
					const sollPart = ` · Soll ${fmt(c.sollValues[i])}`;
					const egfzPart = c.egfzValues[i] > 0 ? ` · davon EGFZ ${fmt(c.egfzValues[i])}` : '';
					return `${datum}${istPart}${sollPart}${egfzPart}`;
				},
			}
		);
	}

	/**
	 * Tagesbalken: Ist (Werktag blau, Wochenende hellblau, Spitzentag rot),
	 * künftige Tage als blasser Plan-Balken auf KostenSoll. Today-Marker und
	 * Spitzentag-Label überlagern die Balken.
	 */
	_renderBarsHtml(c) {
		const W = 340, H = 150, padT = 16, padB = 22, padL = 32, padR = 6;
		const innerW = W - padL - padR, innerH = H - padT - padB;
		const n = c.days.length;
		const bw = innerW / n;
		const max = Math.max(...c.days.map((d) => Math.max(d.ist, d.soll)), 1);
		const niceMax = niceCeil(max);
		const Y = (v) => padT + innerH * (1 - v / niceMax);
		const COL_WEEKEND = '#7cc1e8';
		const COL_PLAN = '#dce6f2';

		const grid = [0, .5, 1].map((t) => {
			const y = padT + innerH * (1 - t);
			const label = t === 0 ? '' : euroCompact(niceMax * t);
			return `<line class="grid-line" x1="${padL}" y1="${y.toFixed(1)}" x2="${W}" y2="${y.toFixed(1)}"/>
				<text class="axis-label" x="${padL - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end">${label}</text>`;
		}).join('');

		const fmtEuro = (v) => euro(v, { maximumFractionDigits: 0 });
		const renderBar = (bxVal, w, value, fill, tooltipText) => {
			const h = innerH * (value / niceMax);
			const y = padT + innerH - h;
			return `<rect x="${bxVal.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="1.5" style="fill:${fill}"><title>${tooltipText}</title></rect>`;
		};
		const bars = c.days.map((d, i) => {
			const x = padL + i * bw;
			const datum = dayjs(d.date).format('dd, D. MMMM');
			const egfzPart = d.egfz > 0 ? ` · davon EGFZ ${fmtEuro(d.egfz)}` : '';

			// Aktueller Tag: Plan-Balken als blasser Hintergrund, Ist-Balken in
			// derselben Position davor – Ist "wächst in den Plan hinein". Bleibt
			// Ist unter Plan, schaut der pale Rest oben heraus; übersteigt Ist den
			// Plan, ragt es darüber hinaus und macht die Überschreitung sichtbar.
			if (i === c.todayIdx && !d.future) {
				const istFill = i === c.peakIdx ? C_RED : d.weekend ? COL_WEEKEND : C_BLUE;
				const istLabel = i === c.peakIdx ? 'Spitzentag' : 'Ist';
				const istSuffix = d.weekend ? ' (Wochenende)' : '';
				const planTip = `${datum} · Plan ${fmtEuro(d.soll)}${egfzPart}`;
				const istTip = `${datum} · ${istLabel} ${fmtEuro(d.ist)}${istSuffix}${egfzPart}`;
				const bx = x + bw * 0.16;
				const bWidth = bw * 0.68;
				return renderBar(bx, bWidth, d.soll, COL_PLAN, planTip)
					+ (d.ist > 0 ? renderBar(bx, bWidth, d.ist, istFill, istTip) : '');
			}

			const value = d.future ? d.soll : d.ist;
			if (!value) return '';
			let fill;
			if (d.future) fill = COL_PLAN;
			else if (i === c.peakIdx) fill = C_RED;
			else if (d.weekend) fill = COL_WEEKEND;
			else fill = C_BLUE;
			const valueLabel = d.future ? 'Plan' : (i === c.peakIdx ? 'Spitzentag' : 'Ist');
			const suffix = d.weekend && !d.future ? ' (Wochenende)' : '';
			const tooltip = `${datum} · ${valueLabel} ${fmtEuro(value)}${suffix}${egfzPart}`;
			const bx = x + bw * 0.16;
			const bWidth = bw * 0.68;
			return renderBar(bx, bWidth, value, fill, tooltip);
		}).join('');

		// Sparse X-Achse: 1., 7., 14., 21., 28. (+ letzter Tag wenn nicht zu dicht)
		const fixedDays = new Set([1, 7, 14, 21, 28]);
		if (c.days.length > 0 && !fixedDays.has(c.days[c.days.length - 1].day) && c.days[c.days.length - 1].day - 28 >= 2) {
			fixedDays.add(c.days[c.days.length - 1].day);
		}
		const xlabels = c.days.map((d, i) => (fixedDays.has(d.day)
			? `<text class="axis-label" x="${(padL + i * bw + bw / 2).toFixed(1)}" y="${H - 7}" text-anchor="middle">${d.day}.</text>`
			: '')).join('');

		// Today-Marker auf der rechten Kante des heutigen Balkens.
		let todayMarker = '';
		if (c.todayIdx >= 0) {
			const tx = padL + (c.todayIdx + 1) * bw;
			todayMarker = `<line x1="${tx.toFixed(1)}" y1="${padT}" x2="${tx.toFixed(1)}" y2="${padT + innerH}" style="stroke:#c2cede" stroke-width="1" stroke-dasharray="3 3"/>
				<text class="axis-label" x="${tx.toFixed(1)}" y="${padT - 3}" text-anchor="middle" style="fill:#8aa0bd">heute</text>`;
		}

		// Wert über dem Spitzentag-Balken.
		let peakLabel = '';
		if (c.peakIdx >= 0) {
			const d = c.days[c.peakIdx];
			const px = padL + c.peakIdx * bw + bw / 2;
			peakLabel = `<text class="bar-peak-label" x="${px.toFixed(1)}" y="${(Y(d.ist) - 4).toFixed(1)}" text-anchor="middle">${euroCompact(d.ist)}</text>`;
		}

		const legendItems = [
			[C_BLUE, 'Werktag'],
			[COL_WEEKEND, 'Wochenende'],
		];
		if (c.peakIdx >= 0) legendItems.push([C_RED, 'Spitzentag']);
		if (c.days.some((d) => d.future)) legendItems.push([COL_PLAN, 'geplant']);
		const legend = `<div class="chart-legend">${legendItems.map(([col, label]) => `<span><span class="dot" style="background:${col}"></span>${label}</span>`).join('')}</div>`;

		return `<div class="chart-wrap">
			<svg class="chart-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet">${grid}${bars}${todayMarker}${peakLabel}${xlabels}</svg>
			${legend}
		</div>`;
	}
}
